import React from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader.tsx';
import { ElectronicSignature } from '~/components/SignatureVerification/ElectronicSignature/ElectronicSignature.tsx';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import FlowSection from '../../components/root/shared/FlowSection.tsx';
import ApplicationHeader from '../../shared/components/ApplicationHeader.tsx';
import ApplicationPageFrame from '../../shared/components/ApplicationPageFrame.tsx';
import { useFreshGuaranteesRequestError } from '../../shared/hooks/useFreshGuaranteesRequestError.ts';
import {
  MANAGER_STEP3_LOADING_TEXT,
} from '../../manager/step3/constants.ts';
import ClientStepNavigator from '../stepNavigation/ClientStepNavigator.tsx';
import { CLIENT_APPROVAL_LETTER_SIGN_TEXT } from './constants.ts';
import { useClientApprovalLetterSignPage } from './useClientApprovalLetterSignPage.ts';

const ClientApprovalLetterSignPage: React.FC = () => {
  const {
    applicationError,
    contragentId,
    deepLink,
    documents,
    effectiveApplicationId,
    headerProps,
    isDocumentLoading,
    isSignatureModalOpen,
    isSuccessRedirectLocked,
    isSigningLoading,
    isStepLoading,
    modalAlert,
    modalSuccess,
    pdfUrl,
    redirectPath,
    timerRef,
    handleCloseSignatureModal,
    handleDiiaSignature,
    handleIITSignature,
    handleOpenPdf,
    handleSign,
    handleTimeUp,
    onIITSign,
    onModalClose,
    onSuccessModalClose,
    showGuaranteesRequestError,
  } = useClientApprovalLetterSignPage();

  useFreshGuaranteesRequestError(applicationError, showGuaranteesRequestError);

  if (redirectPath && !modalSuccess.text && !isSuccessRedirectLocked) {
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <ApplicationPageFrame
      modalAlert={modalAlert}
      modalSuccess={modalSuccess}
      onModalClose={onModalClose}
      onSuccessModalClose={onSuccessModalClose}
    >
      {isSigningLoading && <FullPageLoader />}
      <ElectronicSignature
        isModalOpen={isSignatureModalOpen}
        handleCloseModal={handleCloseSignatureModal}
        handleSignatureDiia={handleDiiaSignature}
        handleSignatureIIT={handleIITSignature}
        deepLink={deepLink}
        onIITSign={onIITSign}
        handleTimeUp={handleTimeUp}
        handlePreview={handleOpenPdf}
        ref={timerRef}
        documents={documents}
        waitForDiiaInitialization
      />
      <ApplicationHeader {...headerProps} />

      {isStepLoading ? (
        <Box sx={commonStyles.centeredLoader}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={guaranteesClientPageStyles.applicationFlowStack}>
          <FlowSection
            step={CLIENT_APPROVAL_LETTER_SIGN_TEXT.sectionStep}
            title={CLIENT_APPROVAL_LETTER_SIGN_TEXT.sectionTitle}
            description={CLIENT_APPROVAL_LETTER_SIGN_TEXT.sectionDescription}
          >
            <Box sx={guaranteesClientPageStyles.generatedDocumentPreview}>
              {isDocumentLoading ? (
                <Box sx={guaranteesClientPageStyles.flowLoadingState}>
                  <CircularProgress size={24} />
                  {MANAGER_STEP3_LOADING_TEXT}
                </Box>
              ) : (
                <Box
                  component="iframe"
                  src={pdfUrl || undefined}
                  title={CLIENT_APPROVAL_LETTER_SIGN_TEXT.previewTitle}
                  sx={guaranteesClientPageStyles.generatedDocumentIframe}
                />
              )}

              <Box sx={guaranteesClientPageStyles.generatedDocumentActions}>
                <Button
                  variant="outlined"
                  color="primary"
                  type="button"
                  sx={guaranteesClientPageStyles.generatedDocumentOpenButton}
                  disabled={!pdfUrl}
                  onClick={handleOpenPdf}
                >
                  {CLIENT_APPROVAL_LETTER_SIGN_TEXT.openButton}
                </Button>
              </Box>
            </Box>
          </FlowSection>

          <Box sx={guaranteesClientPageStyles.flowPageActionsCentered}>
            <Button
              variant="contained"
              color="primary"
              type="button"
              sx={guaranteesClientPageStyles.generatedDocumentOpenButton}
              onClick={handleSign}
            >
              {CLIENT_APPROVAL_LETTER_SIGN_TEXT.signButton}
            </Button>
          </Box>

          <ClientStepNavigator
            applicationId={effectiveApplicationId}
            contragentId={contragentId}
            currentStepKey="step3"
          />
        </Box>
      )}
    </ApplicationPageFrame>
  );
};

export default ClientApprovalLetterSignPage;
